import React, { useState, useEffect } from "react";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import { Table, Spin, Empty, Button, Tooltip, message, Select, Tag } from "antd";
import moment from "moment";
import axios from "axios";
import Chart from "react-apexcharts";
import {
  AiOutlineDollarCircle,
  AiOutlineReload,
  AiOutlineExclamationCircle,
  AiOutlineCheckCircle,
  AiOutlineTag
} from "react-icons/ai";
import { serverUrl } from "../../../../../nestserver";

const { Option } = Select;

const FEE_TYPES = [
  { value: "tution", label: "Tuition" },
  { value: "transportation", label: "Transportation" },
  { value: "accommodation", label: "Accommodation" },
  { value: "other", label: "Miscellaneous Fee" },
];

const MONTHS = [
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const FEE_TYPE_META = {
  tution: { label: "Tuition", color: "blue" },
  transportation: { label: "Transport", color: "cyan" },
  accommodation: { label: "Accommodation", color: "purple" },
  other: { label: "Miscellaneous Fee", color: "orange" },
};

const PAYMENT_MODE_META = {
  cash: { label: "Cash", color: "green" },
  card: { label: "Card", color: "blue" },
  online: { label: "Online", color: "purple" },
  cheque: { label: "Cheque", color: "orange" },
  bank_transfer: { label: "Bank Transfer", color: "cyan" },
  razorpay: { label: "Razorpay", color: "magenta" },
  refund: { label: "Refund", color: "red" },
};

const FeeDashboard = () => {
  const getCurrentAcademicYear = () => {
    const today = new Date();
    const curYear = today.getFullYear();
    const curMonth = today.getMonth() + 1;
    const startYear = curMonth >= 6 ? curYear : curYear - 1;
    const endYearShort = String(startYear + 1).slice(-2);
    return `${startYear}-${endYearShort}`;
  };

  const academicYears = [getCurrentAcademicYear()];

  const [selectedYear, setSelectedYear] = useState(getCurrentAcademicYear());
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedFeeType, setSelectedFeeType] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);

  // Analytics state
  const [summary, setSummary] = useState({
    totalExpected: 0,
    totalCollected: 0,
    totalRemaining: 0,
    totalDiscount: 0,
    totalStudents: 0,
    collectionRate: 0,
    otherExpected: 0,
    otherCollected: 0,
    otherRemaining: 0,
    otherDiscount: 0,
    otherCollectionRate: 0,
  });
  const [barChartData, setBarChartData] = useState([]);
  const [pieChartData, setPieChartData] = useState({
    totalExpected: 0,
    totalApproved: 0,
    totalPending: 0,
    approvedPercentage: 0,
    pendingPercentage: 0,
  });
  const [recentTransactions, setRecentTransactions] = useState([]);

  // ─── Fetch classes for dropdown ────────────────────────────────────────────
  const fetchClasses = async () => {
    try {
      const res = await axios.get(`${serverUrl}/classes`, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      });
      setClasses(res.data || []);
    } catch (e) {
      // Silently fail – class filter will remain empty
    }
  };

  const fetchDashboardData = async (year, classId, feeType, month) => {
    setLoading(true);
    const token = window.localStorage.getItem("jwt-token");
    const headers = { Authorization: `Bearer ${token}` };

    const params = new URLSearchParams();
    if (year) params.append("year", year);
    if (classId) params.append("classId", classId);
    if (feeType) params.append("feeType", feeType);
    if (month) params.append("month", month);
    const query = params.toString() ? `?${params.toString()}` : "";

    try {
      const [summaryRes, trendRes, recentRes] = await Promise.all([
        axios.get(`${serverUrl}/fee-analytics/summary${query}`, { headers }),
        axios.get(`${serverUrl}/fee-analytics/monthly-trend${query}`, { headers }),
        axios.get(`${serverUrl}/fee-analytics/recent-transactions${query}`, { headers }),
      ]);

      if (summaryRes.data?.result) setSummary(summaryRes.data.result);
      if (trendRes.data?.result) {
        setBarChartData(trendRes.data.result.barChart || []);
        setPieChartData(trendRes.data.result.pieChart || {});
      }
      if (recentRes.data?.result) setRecentTransactions(recentRes.data.result);
    } catch (error) {
      console.error("Error fetching fee analytics data:", error);
      message.error("Failed to fetch dashboard metrics. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    fetchDashboardData(selectedYear, selectedClass, selectedFeeType, selectedMonth);
  }, [selectedYear, selectedClass, selectedFeeType, selectedMonth]);

  const formatCurrency = (val) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(val || 0);

  const barChartConfig = {
    options: {
      chart: {
        id: "monthly-trend-bar",
        type: "bar",
        toolbar: { show: false },
        zoom: { enabled: false },
        redrawOnParentResize: false,
      },
      colors: ["#6366f1", "#fca5a5"],
      plotOptions: {
        bar: { columnWidth: "55%", borderRadius: 4, grouped: true },
      },
      dataLabels: { enabled: false },
      xaxis: {
        categories: barChartData.map((d) => d.month),
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { fontSize: "12px", fontWeight: 500 } },
      },
      yaxis: {
        labels: { formatter: (v) => formatCurrency(v) },
      },
      tooltip: {
        y: { formatter: (v) => formatCurrency(v) },
      },
      legend: {
        position: "top",
        horizontalAlign: "right",
        fontWeight: 600,
        fontSize: "12px",
      },
      grid: { borderColor: "#f1f5f9", strokeDashArray: 4 },
    },
    series: [
      {
        name: "Collected (Approved)",
        data: barChartData.map((d) => d.paid),
      },
      {
        name: "Remaining",
        data: barChartData.map((d) => d.remaining),
      },
    ],
  };

  const hasChartData =
    (pieChartData.approvedPercentage || 0) +
      (pieChartData.pendingPercentage || 0) +
      (pieChartData.discountPercentage || 0) >
    0;

  const donutChartConfig = {
    options: {
      chart: { id: "fee-collection-donut", redrawOnParentResize: false },
      labels: ["Collected", "Pending", "Discount"],
      colors: ["#10b981", "#f87171", "#fbbf24"],
      legend: { position: "bottom", fontWeight: 600 },
      plotOptions: {
        pie: {
          customScale: 0.92,
          donut: {
            size: "65%",
            labels: {
              show: true,
              name: { show: true, fontSize: "13px", fontWeight: 600 },
              value: {
                show: true,
                fontSize: "18px",
                fontWeight: 700,
                formatter: (val) => `${parseFloat(val).toFixed(1)}%`,
              },
              total: {
                show: true,
                label: "Collected",
                color: "#10b981",
                fontWeight: 700,
                formatter: () =>
                  `${pieChartData.approvedPercentage || 0}%`,
              },
            },
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val.toFixed(1)}%`,
        style: { fontSize: "11px", fontWeight: 600 },
      },
      stroke: { width: 2 },
    },
    series: [
      pieChartData.approvedPercentage || 0,
      pieChartData.pendingPercentage || 0,
      pieChartData.discountPercentage || 0,
    ],
  };

  const transactionColumns = [
    {
      title: "Txn ID",
      dataIndex: "transactionId",
      key: "transactionId",
      render: (id) => (
        <span className="font-mono text-xs text-indigo-600 font-semibold">
          {id || "—"}
        </span>
      ),
    },
    {
      title: "Student",
      key: "student",
      render: (_, record) => (
        <div>
          <span className="font-semibold text-slate-800 text-sm block">
            {record.studentName || "—"}
          </span>
        </div>
      ),
    },
    {
      title: "Class",
      dataIndex: "className",
      key: "className",
      render: (text) => (
        <span className="px-2 py-1 bg-slate-100 rounded text-xs font-semibold text-slate-600">
          {text || "N/A"}
        </span>
      ),
    },
    {
      title: "Fee Type",
      dataIndex: "feeType",
      key: "feeType",
      render: (type) => {
        const meta = FEE_TYPE_META[type] || { label: type, color: "default" };
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: "Fee Name",
      dataIndex: "feeName",
      key: "feeName",
      render: (name) => (
        <span className="text-slate-600 text-xs">{name || "—"}</span>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => (
        <span className="font-bold text-emerald-600 text-sm">
          {formatCurrency(amount)}
        </span>
      ),
    },
    {
      title: "Mode",
      dataIndex: "paymentMode",
      key: "paymentMode",
      render: (mode) => {
        const meta = PAYMENT_MODE_META[mode] || {
          label: mode,
          color: "default",
        };
        return (
          <Tag color={meta.color} style={{ textTransform: "capitalize" }}>
            {meta.label}
          </Tag>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date) => (
        <span className="text-slate-500 text-xs">
          {date ? moment(date).format("DD MMM YYYY") : "—"}
          <span className="block text-slate-400">
            {date ? moment(date).format("hh:mm A") : ""}
          </span>
        </span>
      ),
    },
  ];

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="bg-slate-50 min-h-screen p-6">
      {/* ── Header ── */}
      <div
        className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4"
        style={{ marginBottom: "32px" }}
      >
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">
            Fee Collections Dashboard
          </h1>
          <p className="text-slate-500 mt-1">
            Real-time analytical summaries and financial health metrics.
          </p>
        </div>

        {/* ── Filters ── */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Class */}
          <Select
            placeholder="All Classes"
            allowClear
            style={{ minWidth: 150 }}
            value={selectedClass}
            onChange={(val) => setSelectedClass(val || null)}
          >
            {classes.map((cls) => (
              <Option key={cls._id} value={cls._id}>
                {cls.name}
              </Option>
            ))}
          </Select>

          {/* Fee Type */}
          <Select
            placeholder="All Fee Types"
            allowClear
            style={{ minWidth: 160 }}
            value={selectedFeeType}
            onChange={(val) => setSelectedFeeType(val || null)}
          >
            {FEE_TYPES.map((ft) => (
              <Option key={ft.value} value={ft.value}>
                {ft.label}
              </Option>
            ))}
          </Select>

          {/* Month */}
          <Select
            placeholder="All Months"
            allowClear
            style={{ minWidth: 140 }}
            value={selectedMonth}
            onChange={(val) => setSelectedMonth(val || null)}
          >
            {MONTHS.map((m) => (
              <Option key={m.value} value={m.value}>
                {m.label}
              </Option>
            ))}
          </Select>

          {/* Year */}
          <Select
            style={{ minWidth: 120 }}
            value={selectedYear}
            onChange={(val) => setSelectedYear(val)}
            className="rounded-lg shadow-sm border-slate-200"
          >
            {academicYears.map((yr) => (
              <Option key={yr} value={yr}>
                {yr}
              </Option>
            ))}
          </Select>

          {/* Refresh */}
          <Tooltip title="Refresh metrics">
            <Button
              type="default"
              shape="circle"
              icon={
                <AiOutlineReload className={loading ? "animate-spin" : ""} />
              }
              onClick={() =>
                fetchDashboardData(
                  selectedYear,
                  selectedClass,
                  selectedFeeType,
                  selectedMonth
                )
              }
              className="flex items-center justify-center hover:border-indigo-500 hover:text-indigo-500"
            />
          </Tooltip>
        </div>
      </div>

      {/* ── Loading ── */}
      {loading && (
        <div className="flex justify-center items-center h-96">
          <Spin size="large" tip="Aggregating collection data..." />
        </div>
      )}

      {/* ── Dashboard Content ── */}
      {!loading && (
        <>
          {/* KPI Cards */}
          <div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4"
            style={{ marginBottom: "32px" }}
          >
            {/* Expected */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
              <div className="flex justify-between items-center mb-3">
                <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider">
                  Expected
                </span>
                <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg">
                  <AiOutlineDollarCircle size={18} />
                </div>
              </div>
              <h3 className="text-xl font-bold text-slate-800">
                {formatCurrency(summary.totalExpected)}
              </h3>
              <p className="text-slate-400 text-xs mt-1">
                Total scheduled fees
              </p>
            </div>

            {/* Collected */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
              <div className="flex justify-between items-center mb-3">
                <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider">
                  Collected
                </span>
                <div className="p-1.5 bg-emerald-50 text-emerald-600 rounded-lg">
                  <AiOutlineCheckCircle size={18} />
                </div>
              </div>
              <h3 className="text-xl font-bold text-slate-800">
                {formatCurrency(summary.totalCollected)}
              </h3>
              <p className="text-emerald-600 text-xs font-semibold mt-1">
                {summary.collectionRate}% Collection Rate
              </p>
            </div>

            {/* Remaining */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
              <div className="flex justify-between items-center mb-3">
                <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider">
                  Outstanding Fee
                </span>
                <div className="p-1.5 bg-rose-50 text-rose-600 rounded-lg">
                  <AiOutlineExclamationCircle size={18} />
                </div>
              </div>
              <h3 className="text-xl font-bold text-rose-600">
                {formatCurrency(summary.totalRemaining)}
              </h3>
              <p className="text-slate-400 text-xs mt-1">Pending Collection</p>
            </div>

            {/* Discount */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
              <div className="flex justify-between items-center mb-3">
                <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider">
                  Discount
                </span>
                <div className="p-1.5 bg-sky-50 text-sky-600 rounded-lg">
                  <AiOutlineTag size={18} />
                </div>
              </div>
              <h3 className="text-xl font-bold text-slate-800">
                {formatCurrency(summary.totalDiscount)}
              </h3>
              <p className="text-slate-400 text-xs mt-1">Total discounts</p>
            </div>

            {/* Other Fee */}
            {/* <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
              <div className="flex justify-between items-center mb-3">
                <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider">
                  Miscellaneous Fees
                </span>
                <div className="p-1.5 bg-amber-50 text-amber-600 rounded-lg">
                  <AiOutlineDollarCircle size={18} />
                </div>
              </div>
              <h3 className="text-xl font-bold text-slate-800">
                {formatCurrency(summary.otherCollected)}
              </h3>
              <p className="text-xs font-bold mt-1" style={{color: "orange"}}>
                {summary.otherCollectionRate}% Col. of {formatCurrency(summary.otherExpected)} expected
              </p>
            </div> */}
          </div>

          {/* Charts Row */}
          <div
            className="grid grid-cols-1 lg:grid-cols-3 gap-6"
            style={{ marginBottom: "32px" }}
          >
            {/* Bar Chart — Monthly Collected vs Remaining (2/3 width) */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 lg:col-span-2">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-lg font-bold text-slate-800">
                    Monthly Collection Trend
                  </h3>
                  <p className="text-slate-400 text-xs mt-0.5">
                    Approved collections vs remaining balance per month
                  </p>
                </div>
              </div>
              {barChartData.length > 0 ? (
                <Chart
                  options={barChartConfig.options}
                  series={barChartConfig.series}
                  type="bar"
                  height={320}
                />
              ) : (
                <div className="flex justify-center items-center h-80">
                  <Empty description="No collection data for this period" />
                </div>
              )}
            </div>

            {/* Donut Chart — Approved vs Pending % (1/3 width) */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <h3 className="text-lg font-bold text-slate-800 mb-1">
                Collection Status
              </h3>
              <p className="text-slate-400 text-xs mb-4">
                Approved vs pending percentage (full year)
              </p>

              {hasChartData ? (
                <>
                  <Chart
                    options={donutChartConfig.options}
                    series={donutChartConfig.series}
                    type="donut"
                    height={260}
                  />
                  {/* Summary rows */}
                  <div className="mt-3 space-y-2 px-2">
                    <div className="flex justify-between items-center text-xs">
                      <span className="flex items-center gap-1.5 text-emerald-600 font-semibold">
                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" />
                        Collected
                      </span>
                      <span className="font-bold text-emerald-600">
                        {formatCurrency(pieChartData.totalApproved)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="flex items-center gap-1.5 text-rose-500 font-semibold">
                        <span className="w-2.5 h-2.5 rounded-full bg-rose-400 inline-block" />
                        Pending
                      </span>
                      <span className="font-bold text-rose-500">
                        {formatCurrency(pieChartData.totalPending)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="flex items-center gap-1.5 text-amber-500 font-semibold">
                        <span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block" />
                        Discount
                      </span>
                      <span className="font-bold text-amber-500">
                        {formatCurrency(pieChartData.totalDiscount)}
                      </span>
                    </div>
                    <div className="border-t border-slate-100 pt-2 flex justify-between items-center text-xs">
                      <span className="text-slate-500 font-semibold">
                        Total Expected
                      </span>
                      <span className="font-bold text-slate-700">
                        {formatCurrency(pieChartData.totalExpected)}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex justify-center items-center h-72">
                  <Empty description="No collection data available" />
                </div>
              )}
            </div>
          </div>

          {/* Recent Transactions Table */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-bold text-slate-800">
                  Recent Transactions
                </h3>
                <p className="text-slate-400 text-xs mt-0.5">
                  Last 15 approved fee payments
                </p>
              </div>
              <span className="text-xs text-slate-500 bg-slate-100 px-3 py-1 rounded-full font-semibold">
                {recentTransactions.length} records
              </span>
            </div>
            <Table
              dataSource={recentTransactions}
              columns={transactionColumns}
              pagination={false}
              rowKey="_id"
              size="small"
              className="custom-table"
              locale={{
                emptyText: (
                  <Empty description="No recent transactions found for this period" />
                ),
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default FeeDashboard;
